import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import logo from '../../assets/logo.svg';

const CLIENT_ID = process.env.REACT_APP_CLIENT_ID || '9fbqtoeasdj4qo8gkk3neb4jd';
const COGNITO_ENDPOINT = 'https://cognito-idp.us-east-1.amazonaws.com/';

// Map Cognito error codes to user-friendly messages
const ERROR_MAP = {
  NotAuthorizedException: 'Invalid email or password',
  UserNotFoundException: 'Account not found',
  CodeMismatchException: 'Invalid verification code. Check your authenticator app and try again.',
  EnableSoftwareTokenMFAException: 'Invalid verification code. Check your authenticator app and try again.',
  InvalidPasswordException: null, // use message from Cognito
  ExpiredCodeException: 'Session expired. Please sign in again.',
  UserNotConfirmedException: 'Account not confirmed. Contact your administrator.',
  LimitExceededException: 'Too many attempts. Please wait a few minutes and try again.',
};

function getErrorMessage(err) {
  if (err && err.__type) {
    const code = err.__type.split('#').pop();
    if (code in ERROR_MAP) {
      return ERROR_MAP[code] || err.message || 'Authentication failed';
    }
  }
  return err?.message || 'An unexpected error occurred';
}

async function cognitoRequest(action, payload) {
  const response = await fetch(COGNITO_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (!response.ok) {
    throw data;
  }

  return data;
}

function setAuthCookie(idToken) {
  try {
    const base64Url = idToken.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    const maxAge = payload?.exp ? payload.exp - Math.floor(Date.now() / 1000) : 3600;
    const domain = process.env.REACT_APP_SITE_DOMAIN
      ? '.' + process.env.REACT_APP_SITE_DOMAIN.split('.').slice(-2).join('.')
      : '.bcorex.ninja';
    document.cookie = `bcore_id_token=${idToken}; domain=${domain}; path=/; secure; samesite=lax; max-age=${maxAge}`;
  } catch { /* cookie is best-effort for SSO */ }
}

function storeTokens(authResult, username) {
  const prefix = `CognitoIdentityServiceProvider.${CLIENT_ID}`;
  localStorage.setItem(`${prefix}.LastAuthUser`, username);
  localStorage.setItem(`${prefix}.${username}.idToken`, authResult.IdToken);
  localStorage.setItem(`${prefix}.${username}.accessToken`, authResult.AccessToken);
  localStorage.setItem(`${prefix}.${username}.refreshToken`, authResult.RefreshToken);
  setAuthCookie(authResult.IdToken);
}

function Login({ onLoginSuccess }) {
  const [step, setStep] = useState('credentials'); // credentials | challenge | forgotPassword | resetPassword
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Challenge state
  const [challengeName, setChallengeeName] = useState('');
  const [challengeSession, setChallengeSession] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // MFA setup state
  const [totpSecret, setTotpSecret] = useState('');
  const [showManualKey, setShowManualKey] = useState(false);

  // Forgot password state
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  const resetToCredentials = () => {
    setStep('credentials');
    setPassword('');
    setMfaCode('');
    setNewPassword('');
    setConfirmPassword('');
    setChallengeeName('');
    setChallengeSession('');
    setTotpSecret('');
    setShowManualKey(false);
    setResetCode('');
    setResetSuccess(false);
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await cognitoRequest('InitiateAuth', {
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: CLIENT_ID,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      });

      await handleAuthResult(result);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Shared handler for auth results — routes to next challenge or completes login
  const handleAuthResult = async (result) => {
    if (result.AuthenticationResult) {
      storeTokens(result.AuthenticationResult, email);
      onLoginSuccess();
    } else if (result.ChallengeName === 'MFA_SETUP') {
      // Need to associate a TOTP device before we can complete MFA setup
      const assocResult = await cognitoRequest('AssociateSoftwareToken', {
        Session: result.Session,
      });
      setTotpSecret(assocResult.SecretCode);
      setChallengeeName('MFA_SETUP');
      setChallengeSession(assocResult.Session);
      setStep('challenge');
    } else if (result.ChallengeName) {
      setChallengeeName(result.ChallengeName);
      setChallengeSession(result.Session);
      setStep('challenge');
    } else {
      setError('Unexpected authentication response');
    }
  };

  const handleChallenge = async (e) => {
    e.preventDefault();
    setError('');

    // Validate NEW_PASSWORD_REQUIRED fields
    if (challengeName === 'NEW_PASSWORD_REQUIRED') {
      if (newPassword.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    setLoading(true);

    try {
      // MFA_SETUP: verify the TOTP code, then respond to the challenge
      if (challengeName === 'MFA_SETUP') {
        const verifyResult = await cognitoRequest('VerifySoftwareToken', {
          Session: challengeSession,
          UserCode: mfaCode,
          FriendlyDeviceName: 'Authenticator',
        });

        // After verifying, respond to the MFA_SETUP challenge
        const result = await cognitoRequest('RespondToAuthChallenge', {
          ClientId: CLIENT_ID,
          ChallengeName: 'MFA_SETUP',
          Session: verifyResult.Session,
          ChallengeResponses: {
            USERNAME: email,
          },
        });

        await handleAuthResult(result);
        return;
      }

      let challengeResponses = {};

      if (challengeName === 'SOFTWARE_TOKEN_MFA') {
        challengeResponses = {
          USERNAME: email,
          SOFTWARE_TOKEN_MFA_CODE: mfaCode,
        };
      } else if (challengeName === 'SMS_MFA') {
        challengeResponses = {
          USERNAME: email,
          SMS_MFA_CODE: mfaCode,
        };
      } else if (challengeName === 'NEW_PASSWORD_REQUIRED') {
        challengeResponses = {
          USERNAME: email,
          NEW_PASSWORD: newPassword,
        };
      }

      const result = await cognitoRequest('RespondToAuthChallenge', {
        ClientId: CLIENT_ID,
        ChallengeName: challengeName,
        Session: challengeSession,
        ChallengeResponses: challengeResponses,
      });

      await handleAuthResult(result);
    } catch (err) {
      const msg = getErrorMessage(err);
      // If session expired, reset to credentials
      if (msg.includes('expired') || msg.includes('Invalid session')) {
        setError('Session expired. Please sign in again.');
        resetToCredentials();
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await cognitoRequest('ForgotPassword', {
        ClientId: CLIENT_ID,
        Username: resetEmail,
      });
      setStep('resetPassword');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await cognitoRequest('ConfirmForgotPassword', {
        ClientId: CLIENT_ID,
        Username: resetEmail,
        ConfirmationCode: resetCode,
        Password: newPassword,
      });
      setResetSuccess(true);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const renderChallengeUI = () => {
    if (challengeName === 'SOFTWARE_TOKEN_MFA') {
      return (
        <>
          <p className="login-mfa-prompt">
            Enter the 6-digit code from your authenticator app
          </p>
          <div className="form-group">
            <label className="login-label" htmlFor="totp-code">Verification Code</label>
            <input
              id="totp-code"
              className="login-input login-input-mfa"
              type="text"
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replaceAll(/\D/g, ''))}
              placeholder="000000"
              autoFocus
            />
          </div>
        </>
      );
    }

    if (challengeName === 'SMS_MFA') {
      return (
        <>
          <p className="login-mfa-prompt">
            Enter the verification code sent to your phone via SMS
          </p>
          <div className="form-group">
            <label className="login-label" htmlFor="sms-code">SMS Code</label>
            <input
              id="sms-code"
              className="login-input login-input-mfa"
              type="text"
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replaceAll(/\D/g, ''))}
              placeholder="000000"
              autoFocus
            />
          </div>
        </>
      );
    }

    if (challengeName === 'NEW_PASSWORD_REQUIRED') {
      return (
        <>
          <p className="login-mfa-prompt">
            You must set a new password to continue
          </p>
          <div className="form-group">
            <label className="login-label" htmlFor="new-password">New Password</label>
            <input
              id="new-password"
              className="login-input"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min 8 characters"
              autoFocus
            />
          </div>
          <div className="form-group">
            <label className="login-label" htmlFor="confirm-password">Confirm Password</label>
            <input
              id="confirm-password"
              className="login-input"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password"
            />
          </div>
        </>
      );
    }

    if (challengeName === 'MFA_SETUP') {
      const otpauthUri = `otpauth://totp/B%2FCORE:${encodeURIComponent(email)}?secret=${totpSecret}&issuer=B%2FCORE`;

      return (
        <>
          <p className="login-mfa-prompt">
            Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.), then enter the 6-digit code below.
          </p>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{
              display: 'inline-block',
              padding: 12,
              background: '#ffffff',
              borderRadius: 12,
            }}>
              <QRCodeSVG value={otpauthUri} size={180} />
            </div>
            <button
              type="button"
              className="login-back"
              style={{ marginTop: 8 }}
              onClick={() => setShowManualKey(!showManualKey)}
            >
              {showManualKey ? 'Hide Manual Key' : "Can't scan? Enter key manually"}
            </button>
            {showManualKey && (
              <div style={{
                background: 'var(--bcore-bg-card)',
                border: '1px solid var(--bcore-border)',
                borderRadius: 8,
                padding: '12px 16px',
                marginTop: 8,
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 13,
                letterSpacing: 2,
                color: 'var(--bcore-accent)',
                wordBreak: 'break-all',
                userSelect: 'all',
              }}>
                {totpSecret}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="login-label" htmlFor="mfa-setup-code">Verification Code</label>
            <input
              id="mfa-setup-code"
              className="login-input login-input-mfa"
              type="text"
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replaceAll(/\D/g, ''))}
              placeholder="000000"
              autoFocus
            />
          </div>
        </>
      );
    }

    // Unsupported challenge
    return (
      <div className="login-error">
        Unsupported authentication step: {challengeName}
      </div>
    );
  };

  const isChallengeSubmittable = () => {
    if (challengeName === 'SOFTWARE_TOKEN_MFA' || challengeName === 'SMS_MFA' || challengeName === 'MFA_SETUP') {
      return mfaCode.length === 6;
    }
    if (challengeName === 'NEW_PASSWORD_REQUIRED') {
      return newPassword.length >= 8 && confirmPassword.length > 0;
    }
    return false;
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-logo">
          <img src={logo} alt="B/CORE" style={{ width: 180, height: 'auto' }} />
          <span>Operations Portal</span>
        </div>

        {error && <div className="login-error">{error}</div>}

        {step === 'credentials' && (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="login-label" htmlFor="login-email">Email</label>
              <input
                id="login-email"
                className="login-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                required
                autoFocus
                autoComplete="email"
              />
            </div>
            <div className="form-group">
              <label className="login-label" htmlFor="login-password">Password</label>
              <input
                id="login-password"
                className="login-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                autoComplete="current-password"
              />
            </div>
            <button
              className="login-button"
              type="submit"
              disabled={loading || !email || !password}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
            <button
              type="button"
              className="login-back"
              onClick={() => {
                setError('');
                setResetEmail(email);
                setStep('forgotPassword');
              }}
            >
              Forgot Password?
            </button>
          </form>
        )}

        {step === 'forgotPassword' && (
          <form onSubmit={handleForgotPassword}>
            <p className="login-mfa-prompt">
              Enter your email address and we'll send you a verification code to reset your password.
            </p>
            <div className="form-group">
              <label className="login-label" htmlFor="reset-email">Email</label>
              <input
                id="reset-email"
                className="login-input"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="user@example.com"
                required
                autoFocus
                autoComplete="email"
              />
            </div>
            <button
              className="login-button"
              type="submit"
              disabled={loading || !resetEmail}
            >
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
            <button
              type="button"
              className="login-back"
              onClick={resetToCredentials}
            >
              Back to Sign In
            </button>
          </form>
        )}

        {step === 'resetPassword' && !resetSuccess && (
          <form onSubmit={handleResetPassword}>
            <p className="login-mfa-prompt">
              Enter the verification code sent to your email and choose a new password.
            </p>
            <div className="form-group">
              <label className="login-label" htmlFor="reset-code">Verification Code</label>
              <input
                id="reset-code"
                className="login-input login-input-mfa"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replaceAll(/\D/g, ''))}
                placeholder="000000"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label className="login-label" htmlFor="reset-new-password">New Password</label>
              <input
                id="reset-new-password"
                className="login-input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 8 characters"
              />
            </div>
            <div className="form-group">
              <label className="login-label" htmlFor="reset-confirm-password">Confirm Password</label>
              <input
                id="reset-confirm-password"
                className="login-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
              />
            </div>
            <button
              className="login-button"
              type="submit"
              disabled={loading || resetCode.length < 6 || newPassword.length < 8 || !confirmPassword}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            <button
              type="button"
              className="login-back"
              onClick={resetToCredentials}
            >
              Back to Sign In
            </button>
          </form>
        )}

        {step === 'resetPassword' && resetSuccess && (
          <div>
            <p className="login-mfa-prompt" style={{ color: 'var(--status-active)' }}>
              Password reset successful! You can now sign in with your new password.
            </p>
            <button
              className="login-button"
              type="button"
              onClick={resetToCredentials}
            >
              Back to Sign In
            </button>
          </div>
        )}

        {step === 'challenge' && (
          <form onSubmit={handleChallenge}>
            {renderChallengeUI()}
            {/* Only show submit if it's a supported challenge */}
            {(challengeName === 'SOFTWARE_TOKEN_MFA' ||
              challengeName === 'SMS_MFA' ||
              challengeName === 'NEW_PASSWORD_REQUIRED' ||
              challengeName === 'MFA_SETUP') && (
              <button
                className="login-button"
                type="submit"
                disabled={loading || !isChallengeSubmittable()}
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            )}
            <button
              type="button"
              className="login-back"
              onClick={resetToCredentials}
            >
              Back to Sign In
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default Login;
